import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';

import {numberWithCommas, toTitleCase} from 'js/common/string_helper';
import * as ObjectHelper from 'js/common/object_helper';

const BrandDetails = ({
    brandName,
    type,
    brandIndustry,
    contentUse,
    enums,
    maps,
    campaignDisplayType,
    platformHandle,
    gqlData,
    isLegacyCampaign
}) => {
    const brandNameVal = brandName || 'N/A';
    const platformNameVal = type ? toTitleCase(type) : 'N/A';
    const contentUseVal = ObjectHelper.propOr('N/A', contentUse, enums.ContentUse);

    const brandVerticalVal = ObjectHelper.propOr('N/A', brandIndustry, maps.BrandIndustry);
    const brandSocialHandleVal = platformHandle || 'N/A';

    const contentTypeVal = campaignDisplayType
        ? toTitleCase(R.last(R.split('_', campaignDisplayType)))
        : 'N/A';

    const contentType = isLegacyCampaign ? contentTypeVal : gqlData.contentType;

    const renderPlatform = () => {
        if (isLegacyCampaign) {
            return (
                <div className="brand-detail" style={{width: '100%'}}>
                    <div className="section-details-title">Platform</div>
                    <div className="section-details-value">{platformNameVal}</div>
                </div>
            );
        }

        if (!isLegacyCampaign && gqlData.socialChannel) {
            return (
                <div className="brand-detail" style={{width: '100%'}}>
                    <div className="section-details-title">Platform</div>
                    <div className="section-details-value">{gqlData.socialChannel}</div>
                </div>
            );
        }

        return null;
    };

    const renderDistribution = () => {
        if (!isLegacyCampaign) {
            const hasDistribution = gqlData && gqlData.socialChannel ? 'Yes' : 'No';
            return (
                <div className="brand-detail" style={{width: '100%'}}>
                    <div className="section-details-title">Distribution</div>
                    <div className="section-details-value">{hasDistribution}</div>
                </div>
            );
        }

        return null;
    };

    const typeOfContent = (
        <div className="brand-detail brand-detail-full">
            <div className="section-details-title">Type of Content</div>
            <div className="section-details-value">{contentType}</div>
        </div>
    );

    const renderTypeOfVideoContent = () => {
        if (!isLegacyCampaign && gqlData && gqlData.videoType && gqlData.contentType === 'VIDEO') {
            return (
                <div className="brand-detail brand-detail-full">
                    <div className="section-details-title">Type of Video</div>
                    <div className="section-details-value">{gqlData.videoType}</div>
                </div>
            );
        }

        return null;
    };

    const renderContentTier = () => {
        if (!isLegacyCampaign && gqlData.contentTier) {
            return (
                <div className="brand-detail brand-detail-full">
                    <div className="section-details-title">Type of Equipment</div>
                    <div className="section-details-value">{gqlData.contentTier}</div>
                </div>
            );
        }

        return null;
    };

    const renderContentFormat = () => {
        if (!isLegacyCampaign && gqlData.contentFormat) {
            return (
                <div className="brand-detail brand-detail-full">
                    <div className="section-details-title">Content Format</div>
                    <div className="section-details-value">{gqlData.contentFormat}</div>
                </div>
            );
        }

        return null;
    };

    const renderContentSize = () => {
        if (!isLegacyCampaign && gqlData.contentSize) {
            return (
                <div className="brand-detail brand-detail-full">
                    <div className="section-details-title">Content Size</div>
                    <div className="section-details-value">{gqlData.contentSize}</div>
                </div>
            );
        }

        return null;
    };

    const renderContentUse = () => {
        if (isLegacyCampaign) {
            return (
                <div className="brand-detail" style={{width: '100%'}}>
                    <div className="section-details-title">
                        What is this content being used for?
                    </div>
                    <div className="section-details-value">{contentUseVal}</div>
                </div>
            );
        }

        if (!isLegacyCampaign && gqlData.contentUse) {
            return (
                <div className="brand-detail" style={{width: '100%'}}>
                    <div className="section-details-title">
                        What is this content being used for?
                    </div>
                    <div className="section-details-value">{gqlData.contentUse}</div>
                </div>
            );
        }

        return null;
    };

    const renderContentReUse = () => {
        if (!isLegacyCampaign && gqlData.contentReuse) {
            return (
                <div className="brand-detail brand-detail-full">
                    <div className="section-details-title">Content re-use</div>
                    <div className="section-details-value">{gqlData.contentReuse}</div>
                </div>
            );
        }

        return null;
    };

    const renderEstimatedAdBudget = () => {
        if (gqlData && gqlData.estimatedAdBudget) {
            return (
                <div className="brand-detail brand-detail-full">
                    <div className="section-details-title">Estimated Ad Budget</div>
                    <div className="section-details-value">
                        {`$${numberWithCommas(gqlData.estimatedAdBudget)}`}
                    </div>
                </div>
            );
        }
    };

    const renderCampaignKpi = () => {
        if (gqlData && gqlData.campaignKpi) {
            return (
                <>
                    <div className="brand-detail brand-detail-full">
                        <div className="section-details-title">Primary KPI</div>
                        <div className="section-details-value">
                            {gqlData.campaignKpi.campaignPrimaryKpi}
                        </div>
                    </div>
                    <div className="brand-detail brand-detail-full">
                        <div className="section-details-title">KPI Benchmark</div>
                        <div className="section-details-value">
                            {`${gqlData.campaignKpi.campaignBenchmark}${gqlData.campaignKpi.prefix}`}
                        </div>
                    </div>
                </>
            );
        }
    };

    return (
        <div className="brand-details">
            <div className="section-title">Brand</div>
            <div className="brand-details-grid">
                <div className="brand-detail" style={{width: '100%'}}>
                    <div className="section-details-title">Brand</div>
                    <div className="section-details-value">{brandNameVal}</div>
                </div>
                {renderCampaignKpi()}
                {renderPlatform()}
                {renderDistribution()}
                <div className="brand-detail" style={{width: '100%'}}>
                    <div className="section-details-title">Vertical</div>
                    <div
                        className="section-details-value brand-vertical-value"
                        style={{textTransform: 'capitalize'}}
                    >
                        {brandVerticalVal}
                    </div>
                </div>
                <div className="brand-detail" style={{width: '100%'}}>
                    <div className="section-details-title">Handle</div>
                    <div className="section-details-value">{brandSocialHandleVal}</div>
                </div>
                {typeOfContent}
                {renderTypeOfVideoContent()}
                {renderContentTier()}
                {renderContentFormat()}
                {renderContentSize()}
                {renderContentUse()}
                {renderContentReUse()}
                {renderEstimatedAdBudget()}
            </div>
        </div>
    );
};

BrandDetails.propTypes = {
    brandName: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    brandIndustry: PropTypes.string.isRequired,
    contentUse: PropTypes.string,
    platformHandle: PropTypes.string
};

export default BrandDetails;



// WEBPACK FOOTER //
// ./components/verify_receipt_common/brand_details.js